import { COURT_APPEARANCE_TYPES, getAppearanceFeeInfo } from "./vla-fees-data.js";
import { findBillingFee } from "./billingFeeTable.js";

export const BILLING_PARSER_VERSION = "20260722lacw5";

export const EXTRACT_SCHEMA = {
  type: "object",
  properties: {
    entries: {
      type: "array",
      items: {
        type: "object",
        properties: {
          date: { type: "string", description: "Date of the appearance (DD/MM/YYYY format)" },
          client_name: { type: "string", description: "Client full name as written in the diary (e.g. WONGRAM, Nicole)" },
          grant_type: { type: "string", description: "Grant/funding type from brackets after client name: VC = VLA client, L = Legal Aid, V = victim-funded, VLA = VLA, pending = pending. Leave blank if not shown." },
          court: { type: "string", description: "Full court or tribunal name (e.g. Melbourne Magistrates' Court, Mildura County Court, Supreme Court)" },
          appearance_type: { type: "string", description: "Type of appearance — use the exact term from the diary: Mention, Further Mention, Contest, Plea, Sentence, Sentencing, Bail, Committal Mention, ARC Review, Filing Hearing, Case Assessment Hearing, Sentence Indication, Return for Sentence, ICML, CME, FVIO, etc." },
          lawyer_initials: { type: "string", description: "Initials of the LACW lawyer who appeared (e.g. CC, BJ, JW, BU, MZ, CB, EC, EM, AM, MP, RC)" },
          counsel_briefed: { type: "string", description: "Name of any external counsel briefed, if mentioned" },
          outcome: { type: "string", description: "Outcome or result of the appearance as noted in the diary" },
          next_date: { type: "string", description: "Next court date mentioned in the outcome, if any" },
          claimable: { type: "boolean", description: "True if this entry is a claimable VLA appearance (VC, L or VLA grant type, or clearly a legal aid matter). False for admin notes, public holidays, non-sitting days, remand calendars, or matters with no appearance." },
          atlas_claim_type: { type: "string", description: "Suggested ATLAS claim type. IMPORTANT: If the outcome notes an adjournment to a specific future date for a specific appearance type (e.g. 'adj to 15/4 for contest', 'adjourned to 3 June for plea'), set this to 'Confirm adj type' — the next appearance type is not yet billed. Otherwise use: 'Daily appearance fee' for mentions/adjournments; 'Contest hearing appearance' for contests; 'Appearance on sentence or adjournment' for sentence/return for sentence; 'Bail appearance fee' for bail applications; 'Committal mention / case conference appearance' for committal mentions; 'ARC — Review Hearing' for ARC reviews; 'ARC — Eligibility hearing' for ARC eligibility/finalisation; 'Active Case Management — case assessment hearing' for case assessment hearings; 'Sentence indication' for sentence indications; 'Plea — appearance fee (first day)' for pleas; 'Trial — appearance fee (first day)' for trial day 1; 'Directions hearing / mention / callover' for callover/directions. Leave blank if not claimable." },
          notes: { type: "string", description: "Any other relevant notes, flags or observations from the diary entry" },
        }
      }
    },
    summary: { type: "string", description: "Brief summary: diary period covered, total entries found, total claimable entries" }
  }
};

export const APPEARANCE_COLOR = {
  contest: "bg-red-50 border-red-200 text-red-700",
  plea: "bg-purple-50 border-purple-200 text-purple-700",
  bail: "bg-sky-50 border-sky-200 text-sky-700",
  mention: "bg-slate-50 border-slate-200 text-slate-700",
  conference: "bg-amber-50 border-amber-200 text-amber-700",
  sentence: "bg-orange-50 border-orange-200 text-orange-700",
  default: "bg-green-50 border-green-200 text-green-700",
};

export function getAppearanceColor(type) {
  if (!type) return APPEARANCE_COLOR.default;
  const t = type.toLowerCase();
  for (const key of Object.keys(APPEARANCE_COLOR)) {
    if (t.includes(key)) return APPEARANCE_COLOR[key];
  }
  return APPEARANCE_COLOR.default;
}

// An appearance marked L on LACW's diary - "(L)", or with another mark, "(VU/L)" or
// "(VU + L)" (read by parseDiaryEntryBlock in src/lib/standaloneServices.js). Emma works the
// VU, VC and unmarked appearances first; the L ones are listed after them (21 Oct 2026).
export function isLMarked(grantType = "") {
  return String(grantType || "").toUpperCase().split(/[^A-Z]+/).includes("L");
}

// L-marked appearances are LACW funded (Emma, 23 Oct 2026). Returns the actions to add:
// only appearances with no action yet, so a choice someone has made - including Counsel
// appeared, set from the diary - is never overwritten.
export function lacwFundedDefaults(entries = [], actions = {}) {
  const defaults = {};
  for (const entry of entries) {
    if (entry?.id && !actions[entry.id] && isLMarked(entry.grant_type)) defaults[entry.id] = "LACW funded";
  }
  return defaults;
}

export const GRANT_BADGE = {
  VC: "bg-purple-100 text-purple-700 border-purple-200",
  VU: "bg-amber-100 text-amber-800 border-amber-300",
  L: "bg-blue-100 text-blue-700 border-blue-200",
  V: "bg-pink-100 text-pink-700 border-pink-200",
  VLA: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
};

const uniqueAppearanceTypes = new Set();
Object.values(COURT_APPEARANCE_TYPES).forEach(arr => {
  arr.forEach(item => {
    if (
      item.label &&
      item.label !== "Other" &&
      !item.label.includes("Lump sum preparation") &&
      !item.label.includes("Lump sum consolidated preparation")
    ) {
      uniqueAppearanceTypes.add(item.label);
    }
  });
});

// Add extra custom appearance types if not present
uniqueAppearanceTypes.add("Mention");
uniqueAppearanceTypes.add("Plea");
uniqueAppearanceTypes.add("Consol Plea");
uniqueAppearanceTypes.add("Consol Adjournment");
uniqueAppearanceTypes.add("Consol Sentence");
uniqueAppearanceTypes.add("Sentence");
uniqueAppearanceTypes.add("Contest");
uniqueAppearanceTypes.add("Bail");
uniqueAppearanceTypes.add("Further Mention");
uniqueAppearanceTypes.add("Special Mention");
uniqueAppearanceTypes.add("Contest Mention");
uniqueAppearanceTypes.add("Adjournment");
uniqueAppearanceTypes.add("Preparation");

// What the Billing Sheet calls some of the VLA fee table's appearance types (Emma, 21 Oct
// 2026). Only the name shown changes: the fee is still looked up under the table's own name
// (src/lib/vla-fees-data.js, which other tools read), and a week saved with the old name
// shows the new one.
export const BILLING_TYPE_NAMES = {
  "Daily appearance fee": "Daily Appearance fee - Single PG",
  "Daily appearance fee (consolidation)": "Daily Appearance fee - Consol PG",
};
const VLA_TYPE_FOR_NAME = Object.fromEntries(Object.entries(BILLING_TYPE_NAMES).map(([vla, name]) => [name, vla]));
export const billingTypeName = (value) => BILLING_TYPE_NAMES[value] || value;

// A week's actions with any old type name replaced by the Billing Sheet's own.
export function renameBillingActions(actions = {}) {
  let changed = false;
  const next = {};
  for (const [id, action] of Object.entries(actions || {})) {
    next[id] = billingTypeName(action);
    if (next[id] !== action) changed = true;
  }
  return changed ? next : actions;
}

const sortedAppearanceTypes = Array.from(uniqueAppearanceTypes, billingTypeName).sort();
export const APPEARANCE_TYPE_OPTIONS = [...sortedAppearanceTypes, "Other"];

// A single (not consolidated) adjournment is billed at the VLA Table A single summary
// adjournment rate, whatever the court. It was $185 - the mention rate - until 14 Oct 2026.
export const ADJOURNMENT_FEE = 318;

// A consolidated adjournment or sentence is billed at the VLA consolidated rate
// ("Appearance on sentence or adjournment (consolidation)"), also whatever the court.
export const CONSOL_ADJOURNMENT_OR_SENTENCE_FEE = 369;

// A consolidated plea is billed at the VLA consolidated daily appearance rate
// ("Daily appearance fee (consolidation)").
export const CONSOL_PLEA_FEE = 579;

// Appearance types billed at one flat amount rather than looked up by court.
export const FLAT_APPEARANCE_FEES = {
  "Adjournment": ADJOURNMENT_FEE,
  "Consol Plea": CONSOL_PLEA_FEE,
  "Consol Adjournment": CONSOL_ADJOURNMENT_OR_SENTENCE_FEE,
  "Consol Sentence": CONSOL_ADJOURNMENT_OR_SENTENCE_FEE,
};

// Listed VLA fees the Billing Sheet does not fill in by itself. A Plea in the Magistrates' or
// Children's Court would get the $318 "Plea of guilty" summary rate, which is not the fee for
// that appearance (Emma, 21 Oct 2026): its amount is typed, or picked with "+ Add fee".
// County and Supreme Court pleas keep their listed first-day fee. The fee table itself
// (src/lib/vla-fees-data.js) is unchanged - other tools read it.
export const NO_AUTOMATIC_FEE = {
  Plea: ["Magistrates Court", "Children's Court (Summary)"],
};

// Billing appearance types priced under another fee table name. Plain "Bail" had no fee; it
// is a bail application ($557 in the Magistrates' or Children's Court from 19 Oct 2026).
const FEE_TABLE_TYPE_FOR = { Bail: "Bail Application" };

// "Preparation" asks which VLA lump sum preparation fee applies each time.
export const PREPARATION_FEE_OPTIONS = [
  "Lump sum preparation fee",
  "Lump sum preparation fee (urgent)",
  "Lump sum consolidated preparation fee",
  "Lump sum consolidated preparation fee (urgent)",
];

// Diary courts are written like "SUNSHINE MAGISTRATES' COURT"; the VLA fee table is keyed by jurisdiction.
export function feeCourtForDiaryCourt(court = "") {
  const c = court.toLowerCase();
  if (c.includes("children")) return "Children's Court (Summary)";
  if (c.includes("magistrate")) return "Magistrates Court";
  // The diary's "A REMANDS" list (src/lib/standaloneServices.js extractCourtName) is the
  // Magistrates' Court remand list; it had no fees at all (Emma, 21 Oct 2026: Bail showed
  // "No listed fee").
  if (/\bremands?\b/.test(c)) return "Magistrates Court";
  if (c.includes("county")) return "County Court";
  if (c.includes("court of appeal")) return "Court of appeal";
  if (c.includes("supreme")) return "Supreme Court";
  if (c.includes("high court")) return "High Court";
  return "";
}

const DIARY_APPEARANCE_ALIASES = { m: "Mention", fm: "Further Mention" };

// The appearance type to bill: the one picked in the row's dropdown, otherwise the diary's own type.
export function billingAppearanceType(entry, action) {
  const named = billingTypeName(action);
  if (named && APPEARANCE_TYPE_OPTIONS.includes(named)) return named;
  const raw = (entry?.appearance_type || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  return DIARY_APPEARANCE_ALIASES[lower]
    || APPEARANCE_TYPE_OPTIONS.find(type => type.toLowerCase() === billingTypeName(raw).toLowerCase() || type.toLowerCase() === lower)
    || raw;
}

// The VLA fee for an appearance, or null when the court or type has no listed fee.
export function lookupBillingFee({ court, appearanceType: shownType, preparationType }) {
  // The Billing Sheet's own name for a type is looked up under the fee table's name.
  const appearanceType = VLA_TYPE_FOR_NAME[shownType] || shownType;
  if (appearanceType in FLAT_APPEARANCE_FEES) return FLAT_APPEARANCE_FEES[appearanceType];
  const feeCourt = feeCourtForDiaryCourt(court);
  if (!feeCourt) return null;
  const feeType = appearanceType === "Preparation" ? preparationType : FEE_TABLE_TYPE_FOR[appearanceType] || appearanceType;
  if (!feeType) return null;
  if (NO_AUTOMATIC_FEE[feeType]?.includes(feeCourt)) return null;
  return getAppearanceFeeInfo(feeCourt, feeType)?.fee ?? null;
}

// The entry's main fee: a typed amount if staff entered one, otherwise the listed fee for
// its appearance type.
export function billingMainFee(entry, action, fee = {}) {
  if (fee.amount != null && fee.amount !== "") return Number(fee.amount);
  return lookupBillingFee({
    court: entry?.court,
    appearanceType: billingAppearanceType(entry, action),
    preparationType: fee.preparationType,
  });
}

// A diary entry can carry more fees than its main one - preparation as well as the
// appearance, travel, a consolidated uplift. Each extra line is picked from the fee table
// (src/lib/billingFeeTable.js) and is `quantity x rate`, unless an amount was typed for it.
//   fee.extra = [{ id, feeKey, quantity, amount }]
// An extra fee line carries the date it is for (Emma, 22 Oct 2026) - an appearance, a
// conference, travel - except a preparation fee, which has none. Stored as the date input
// gives it (YYYY-MM-DD) in fee.extra[].date; a line saved before this simply has no date.
export const isPreparationFee = (name = "") => /\bPREP(?:ARATION)?\b/i.test(String(name || ""));

const extraFeeDay = (value = "") => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

export function billingExtraFees(fee = {}) {
  return (Array.isArray(fee?.extra) ? fee.extra : []).filter(Boolean).map((line) => {
    const listed = findBillingFee(line.feeKey);
    const quantity = line.quantity == null || line.quantity === "" ? 1 : Number(line.quantity);
    const typed = line.amount != null && line.amount !== "";
    const calculated = listed && Number.isFinite(quantity) ? Math.round(listed.amount * quantity * 100) / 100 : null;
    return {
      id: line.id,
      feeKey: line.feeKey || "",
      name: listed?.name || "",
      table: listed?.table || "",
      rate: listed?.amount ?? null,
      quantity,
      listedAmount: calculated,
      amount: typed ? Number(line.amount) : calculated,
      date: listed && isPreparationFee(listed.name) ? "" : String(line.date || ""),
    };
  });
}

// Every fee on the entry, main fee first, for showing and printing.
export function billingFeeLines(entry, action, fee = {}) {
  const lines = [];
  const main = billingMainFee(entry, action, fee);
  if (main != null && !Number.isNaN(main)) {
    lines.push({ label: billingAppearanceType(entry, action) || "Fee", amount: main });
  }
  for (const extra of billingExtraFees(fee)) {
    if (extra.amount == null || Number.isNaN(extra.amount)) continue;
    const name = extra.quantity !== 1 ? `${extra.name} x ${extra.quantity}` : extra.name;
    const day = extraFeeDay(extra.date);
    lines.push({ label: day ? `${name} (${day})` : name, amount: extra.amount, date: extra.date });
  }
  return lines;
}

// The fee for a diary entry: everything on it added together, or null if nothing is listed.
export function billingFeeFor(entry, action, fee = {}) {
  const lines = billingFeeLines(entry, action, fee);
  if (lines.length === 0) return null;
  return Math.round(lines.reduce((sum, line) => sum + line.amount, 0) * 100) / 100;
}

// Actions that park an appearance on a list that carries over between billing weeks.
// "Counsel appeared" parks the appearance on Awaiting fee slip until counsel's fee slip arrives.
export const HELD_ACTIONS = ["Aid pending", "To be claimed", "Counsel appeared"];

// An appearance counts as claimed when its row is set to an appearance type (Mention, Plea,
// Adjournment...) or marked Claimed / Billed and closed. Every other action - LACW funded,
// Aid required, Aid pending, To be claimed, Not claimed, Counsel appeared, Query and so on -
// is not a claim.
const CLAIMED_STATUSES = ["Claimed", "Billed and closed"];

// "Claimed" and "Billed and closed" were taken off the action list (Emma, 21 Oct 2026): a
// claim is shown by its appearance type, and a finished grant by the "Grant of aid finalised
// on ATLAS" tick. A week that already uses one still shows it, and still counts it as claimed.
export const RETIRED_ACTIONS = ["Claimed", "Billed and closed"];

// The tick on an appearance, kept in the week's fees with the rest of its billing
// (fees[id].atlasFinalised), and printed on the billing sheet.
export const ATLAS_FINALISED_LABEL = "Grant of aid finalised on ATLAS";
export const isAtlasFinalised = (fee) => Boolean(fee?.atlasFinalised);
export function isClaimedAction(action) {
  return CLAIMED_STATUSES.includes(action) || APPEARANCE_TYPE_OPTIONS.includes(billingTypeName(action));
}

const BASE_ACTION_OPTIONS = [
  { value: "", label: "Set action…" },
  { value: "Not claimed", label: "Not claimed" },
  { value: "Counsel appeared", label: "Counsel appeared" },
  { value: "LACW funded", label: "LACW funded" },
  { value: "Aid required", label: "Aid required" },
  { value: "Extension required", label: "Extension required" },
  { value: "Aid pending", label: "Aid pending" },
  { value: "To be claimed", label: "To be claimed" },
  { value: "ACF lodged", label: "ACF lodged" },
  { value: "Query", label: "Query" },
];

export const ACTION_OPTIONS = [
  ...BASE_ACTION_OPTIONS,
  ...APPEARANCE_TYPE_OPTIONS.map(type => ({ value: type, label: type }))
];

// The list for one appearance's dropdown: a retired action it already has is kept at the top,
// greyed out, so the row does not go blank - but it cannot be chosen again.
export function actionOptionsFor(action) {
  return RETIRED_ACTIONS.includes(action)
    ? [ACTION_OPTIONS[0], { value: action, label: action, disabled: true }, ...ACTION_OPTIONS.slice(1)]
    : ACTION_OPTIONS;
}

// The report's filter still offers the retired actions, for weeks that used them.
export const REPORT_ACTION_OPTIONS = [
  ...ACTION_OPTIONS,
  ...RETIRED_ACTIONS.map(value => ({ value, label: value })),
];

const BASE_ACTION_STYLE = {
  "Claimed": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "Billed and closed": "bg-indigo-100 text-indigo-700 border-indigo-300",
  "Not claimed": "bg-slate-100 text-slate-500 border-slate-200",
  "Counsel appeared": "bg-sky-100 text-sky-700 border-sky-300",
  "LACW funded": "bg-purple-100 text-purple-700 border-purple-300",
  "Aid required": "bg-red-100 text-red-700 border-red-300",
  "Extension required": "bg-orange-100 text-orange-700 border-orange-300",
  "Aid pending": "bg-amber-100 text-amber-700 border-amber-300",
  "To be claimed": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "ACF lodged": "bg-teal-100 text-teal-700 border-teal-300",
  "Query": "bg-blue-100 text-blue-700 border-blue-300",
  "Mention": "bg-slate-100 text-slate-700 border-slate-300",
  "Consol Plea": "bg-violet-100 text-violet-700 border-violet-300",
  "Consol Adjournment": "bg-violet-100 text-violet-700 border-violet-300",
  "Consol Sentence": "bg-violet-100 text-violet-700 border-violet-300",
  "Plea": "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300",
};

export const ACTION_STYLE = {
  ...BASE_ACTION_STYLE
};

sortedAppearanceTypes.forEach(type => {
  if (!ACTION_STYLE[type]) {
    ACTION_STYLE[type] = "bg-purple-50 text-purple-700 border-purple-200";
  }
});

export const STAFF = [
  { name: "Ashleigh McPhail", email: "amcphail@lacw.org.au" },
  { name: "Laura Heffes", email: "lheffes@lacw.org.au" },
  { name: "Ellen Murphy", email: "emurphy@lacw.org.au" },
  { name: "Ellie Pappas", email: "epappas@lacw.org.au" },
];
